/**
 * <cart-timer> — a countdown implying the cart is held for a limited time.
 * This is a common urgency cue, not a literal inventory reservation: nothing
 * actually expires when it reaches zero, it just quietly restarts at 60m 00s.
 * Persisted in sessionStorage so it survives the drawer closing/reopening and
 * navigating between pages within the same browser session.
 */
const DURATION_MS = 60 * 60 * 1000;
const STORAGE_KEY = 'cartReservationExpiresAt';

class CartTimer extends HTMLElement {
  /** @type {number | null} */
  #intervalId = null;

  connectedCallback() {
    this.#ensureExpiry();
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#intervalId !== null) window.clearInterval(this.#intervalId);
  }

  #ensureExpiry() {
    const stored = Number(window.sessionStorage.getItem(STORAGE_KEY));
    if (!stored || stored <= Date.now()) {
      window.sessionStorage.setItem(STORAGE_KEY, String(Date.now() + DURATION_MS));
    }
  }

  #tick() {
    let remaining = Number(window.sessionStorage.getItem(STORAGE_KEY)) - Date.now();

    if (remaining <= 0) {
      window.sessionStorage.setItem(STORAGE_KEY, String(Date.now() + DURATION_MS));
      remaining = DURATION_MS;
    }

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    const display = this.querySelector('[data-cart-timer-value]');

    if (display) {
      // "57m 56s" reads better than "57:56" now that the countdown runs for an hour.
      display.textContent = `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    }
  }
}

if (!customElements.get('cart-timer')) {
  customElements.define('cart-timer', CartTimer);
}
